import React from 'react';
import { Input, Select, H2, Icon } from '@tviklax/ui-components';
import { Section, FormGroup, AddressCard, ToggleWrapper, CompactPreview } from './styles';
import { OrderFormData } from './types';
import { AddressAutocomplete, AddressAutofillFields } from './AddressAutocomplete';
import { useCountries, useStatesForCountry } from './addressData';

interface ShippingInformationProps {
  formData: OrderFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onAddressAutofill: (fields: Partial<OrderFormData>) => void;
}

export const ShippingInformation: React.FC<ShippingInformationProps> = ({
  formData,
  onChange,
  onAddressAutofill,
}) => {
  const countries = useCountries();
  const pickupStateOptions = useStatesForCountry(formData.pickupCountry);
  const deliveryStateOptions = useStatesForCountry(formData.deliveryCountry);

  const handlePickupAddressSelected = (fields: AddressAutofillFields) => {
    onAddressAutofill({
      pickupAddr1: fields.street,
      pickupCity: fields.city,
      pickupState: fields.state,
      pickupZip: fields.postalCode,
      pickupCountry: fields.country,
    });
  };

  const handleDeliveryAddressSelected = (fields: AddressAutofillFields) => {
    onAddressAutofill({
      deliveryAddr1: fields.street,
      deliveryCity: fields.city,
      deliveryState: fields.state,
      deliveryZip: fields.postalCode,
      deliveryCountry: fields.country,
    });
  };
  return (
    <Section>
      <H2 icon={<Icon name="Truck" size={20} color="#f86400" />}>Shipping Information</H2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

        {/* Pickup Section */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ fontWeight: 600 }}>Pickup Details</h4>
            <ToggleWrapper as="label">
              <input
                type="checkbox"
                name="vendorSameAsPickup"
                checked={formData.vendorSameAsPickup}
                onChange={onChange}
              />
              <span>Same as Vendor</span>
            </ToggleWrapper>
          </div>

          {formData.vendorSameAsPickup ? (
            <CompactPreview>
              <strong>{formData.VendorName}</strong><br />
              {formData.vendorAddr1}{formData.vendorAddr2 ? `, ${formData.vendorAddr2}` : ''}<br />
              {formData.vendorCity}, {formData.vendorState}, {formData.vendorCountry} - {formData.vendorZip}
            </CompactPreview>
          ) : (
            <AddressCard>
              <FormGroup $span={6}>
                <AddressAutocomplete
                  label="Street Address"
                  name="pickupAddr1"
                  value={formData.pickupAddr1}
                  onChange={onChange}
                  onAddressSelected={handlePickupAddressSelected}
                  placeholder="Street address"
                  required
                />
              </FormGroup>
              <FormGroup $span={3}>
                <Input label="Apt / Suite" name="pickupAddr2" value={formData.pickupAddr2} onChange={onChange} placeholder="Apt, Suite, etc." />
              </FormGroup>
              <FormGroup $span={3}>
                <Input label="Zip Code" name="pickupZip" value={formData.pickupZip} onChange={onChange} placeholder="Zip" required />
              </FormGroup>
              <FormGroup $span={2}><Input label="City" name="pickupCity" value={formData.pickupCity} onChange={onChange} placeholder="City" required /></FormGroup>
              <FormGroup $span={2}>
                <Select
                  label="State"
                  name="pickupState"
                  value={formData.pickupState}
                  onChange={onChange}
                  items={pickupStateOptions}
                  placeholder="Select State"
                  disabled={pickupStateOptions.length === 0}
                  required={pickupStateOptions.length > 0}
                />
              </FormGroup>
              <FormGroup $span={2}>
                <Select
                  label="Country"
                  name="pickupCountry"
                  value={formData.pickupCountry}
                  onChange={onChange}
                  items={countries}
                  placeholder="Select Country"
                  required
                />
              </FormGroup>
            </AddressCard>
          )}
        </div>

        {/* Delivery Section */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ fontWeight: 600 }}>Delivery Details</h4>
            <ToggleWrapper as="label">
              <input
                type="checkbox"
                name="deliverySameAsCustomer"
                checked={formData.deliverySameAsCustomer}
                onChange={onChange}
              />
              <span>Same as Customer</span>
            </ToggleWrapper>
          </div>

          {formData.deliverySameAsCustomer ? (
            <CompactPreview>
              <strong>{formData.CustomerName}</strong><br />
              {formData.customerAddr1}{formData.customerAddr2 ? `, ${formData.customerAddr2}` : ''}<br />
              {formData.customerCity}, {formData.customerState}, {formData.customerCountry} - {formData.customerZip}
            </CompactPreview>
          ) : (
            <AddressCard>
              <FormGroup $span={6}>
                <AddressAutocomplete
                  label="Street Address"
                  name="deliveryAddr1"
                  value={formData.deliveryAddr1}
                  onChange={onChange}
                  onAddressSelected={handleDeliveryAddressSelected}
                  placeholder="Street address"
                  required
                />
              </FormGroup>
              <FormGroup $span={3}>
                <Input label="Apt / Suite" name="deliveryAddr2" value={formData.deliveryAddr2} onChange={onChange} placeholder="Apt, Suite, etc." />
              </FormGroup>
              <FormGroup $span={3}>
                <Input label="Zip Code" name="deliveryZip" value={formData.deliveryZip} onChange={onChange} placeholder="Zip" required />
              </FormGroup>
              <FormGroup $span={2}><Input label="City" name="deliveryCity" value={formData.deliveryCity} onChange={onChange} placeholder="City" required /></FormGroup>
              <FormGroup $span={2}>
                <Select
                  label="State"
                  name="deliveryState"
                  value={formData.deliveryState}
                  onChange={onChange}
                  items={deliveryStateOptions}
                  placeholder="Select State"
                  disabled={deliveryStateOptions.length === 0}
                  required={deliveryStateOptions.length > 0}
                />
              </FormGroup>
              <FormGroup $span={2}>
                <Select
                  label="Country"
                  name="deliveryCountry"
                  value={formData.deliveryCountry}
                  onChange={onChange}
                  items={countries}
                  placeholder="Select Country"
                  required
                />
              </FormGroup>
            </AddressCard>
          )}
        </div>
      </div>
    </Section>
  );
};
